import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRoundCheck } from 'lucide-react';

import { apiFetch } from '@/lib/api';

/**
 * Standardized, high-signal language for the most common note ("this isn't the right
 * person"). Sent verbatim on an asset_ref so the interpreter deterministically turns
 * it into a rework_keyframe that regenerates the frame against the character sheet.
 *
 * NOTE: this is a MODEL INSTRUCTION, not UI copy — it must never be translated.
 */
const IDENTITY_FIX_NOTE =
  'IDENTITY FIX: the character(s) in this frame do NOT match the established character ' +
  'reference. Regenerate this frame so every character EXACTLY matches their reference ' +
  'sheet — identical face, bone structure, skin tone, ethnicity, age, and hair. Reproduce ' +
  'the reference character faithfully; do not invent or substitute a different person. ' +
  'Preserve the composition, framing, wardrobe intent, and location — correct ONLY the ' +
  'character identity to match the reference.';

/**
 * One-click "Fix Character" — sends the standardized identity-fix note for a keyframe
 * so the frame is reworked to match the character reference. No typing needed (this
 * happens a lot). Becomes a rework_keyframe item in the Work Queue to confirm.
 */
export default function FixCharacterButton({ productionId, shotId }: { productionId?: string; shotId: string }) {
  const { t } = useTranslation('production');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!productionId || sending) return;
    setSending(true);
    try {
      await apiFetch(`/api/chat/${productionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: IDENTITY_FIX_NOTE, asset_ref: `keyframe:${shotId}` }),
      });
      setSent(true);
      setTimeout(() => setSent(false), 1800);
    } finally {
      setSending(false);
    }
  };

  return (
    <button
      onClick={send}
      title={t('tile.fixCharacterTitle')}
      className="flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-medium text-white hover:bg-primary disabled:opacity-60"
      disabled={sending}
    >
      <UserRoundCheck className="h-2.5 w-2.5" />
      {sent ? t('tile.fixCharacterSent') : t('tile.fixCharacter')}
    </button>
  );
}
